import { images } from '../constants';

const services = [
    {
        id: 1,
        image: images.service1,
        title: 'Data Insights',
        content: 'Unlock actionable insights with advanced data analysis and visualization tools.',
        paragraphs: [
            'Empower your organization with data-driven decision-making. Our Data Insights service transforms complex data into clear, actionable visualizations and reports tailored to your business goals.',
            'Using industry-leading analytics tools and custom dashboards, we help you identify trends, optimize processes, and uncover growth opportunities across your operations.'
        ],
        gallery: [
            images.service1Gallery1,
            images.service1Gallery2
        ],
        features: [
            'Custom Data Dashboards',
            'Advanced Analytics & Reporting',
            'Real-Time Data Visualization',
            'Predictive Insights & Forecasting',
            'Secure Cloud-Based Access'
        ],
        process: [
            'Understand Business Goals & KPIs',
            'Collect & Integrate Data from Sources',
            'Analyze & Cleanse Data for Accuracy',
            'Build Custom Dashboards & Reports',
            'Deliver Insights for Decision-Making'
        ],
        faqs: [
            {
                id: 1,
                heading: 'What types of insights can I expect?',
                content: 'We provide business intelligence, sales performance trends, customer behavior analysis, and operational efficiency metrics customized to your industry.'
            },
            {
                id: 2,
                heading: 'How secure is my business data?',
                content: 'Your data is protected with enterprise-grade security protocols and secure cloud infrastructure, ensuring complete confidentiality and compliance.'
            },
            {
                id: 3,
                heading: 'Can I integrate data from multiple platforms?',
                content: 'Yes, we support integration with CRMs, ERPs, marketing tools, and other third-party data platforms for unified analysis.'
            },
            {
                id: 4,
                heading: 'Do you offer real-time analytics?',
                content: 'Yes, we deliver real-time dashboards and automated reports to keep you informed with the latest insights and KPIs.'
            }
        ]
    },
    {
        id: 2,
        image: images.service2,
        title: 'Machine Learning',
        content: 'Build smarter systems with advanced, scalable custom machine learning models.',
        paragraphs: [
            'Harness the power of machine learning to automate processes, predict trends, and personalize customer experiences. Our team develops and deploys models that solve complex problems efficiently.',
            'From data preprocessing to model training and deployment, we ensure your ML solutions are optimized for scalability, performance, and continuous improvement.'
        ],
        gallery: [
            images.service2Gallery1,
            images.service2Gallery2
        ],
        features: [
            'Custom ML Model Development',
            'Predictive & Prescriptive Analytics',
            'Natural Language Processing (NLP)',
            'Image & Speech Recognition',
            'Model Deployment & Monitoring'
        ],
        process: [
            'Define Business Objectives & Use Cases',
            'Collect & Prepare Relevant Data',
            'Select & Train Machine Learning Models',
            'Evaluate & Optimize Model Performance',
            'Deploy & Maintain ML Solutions'
        ],
        faqs: [
            {
                id: 1,
                heading: 'What industries can benefit from machine learning?',
                content: 'Machine learning applies to healthcare, finance, retail, logistics, marketing, and more, helping automate tasks and predict future trends.'
            },
            {
                id: 2,
                heading: 'Can you build custom ML models for my specific needs?',
                content: 'Yes, we develop tailored ML models based on your unique data and business goals to ensure maximum relevance and impact.'
            },
            {
                id: 3,
                heading: 'Do you support ongoing model maintenance?',
                content: 'Absolutely. We offer continuous monitoring, retraining, and optimization services to keep your models performing optimally.'
            },
            {
                id: 4,
                heading: 'How long does it take to develop a machine learning solution?',
                content: 'Project timelines vary based on complexity, but most solutions are delivered in 4-12 weeks, including data preparation and testing.'
            }
        ]
    },
    {
        id: 3,
        image: images.service3,
        title: 'Cloud Automation',
        content: 'Optimize your cloud operations with seamless, automated deployment and dynamic scaling.',
        paragraphs: [
            'Automate your infrastructure deployment, scaling, and monitoring to reduce manual workloads and minimize downtime. Our solutions ensure your cloud environment runs smoothly and adapts to changing demands.',
            'From CI/CD pipelines to serverless computing, we help you achieve faster deployments, consistent configurations, and scalable cloud-native applications.'
        ],
        gallery: [
            images.service3Gallery1,
            images.service3Gallery2
        ],
        features: [
            'Automated Infrastructure Provisioning',
            'CI/CD Pipeline Implementation',
            'Serverless Architecture Support',
            'Auto-Scaling & Load Balancing',
            'Cost Optimization & Resource Management'
        ],
        process: [
            'Assess Cloud Infrastructure & Needs',
            'Design Automation Workflows',
            'Implement CI/CD Pipelines & Scripts',
            'Configure Auto-Scaling & Monitoring',
            'Optimize & Maintain Cloud Operations'
        ],
        faqs: [
            {
                id: 1,
                heading: 'What cloud platforms do you support for automation?',
                content: 'We support AWS, Azure, Google Cloud, and other popular platforms, tailoring solutions to your chosen provider.'
            },
            {
                id: 2,
                heading: 'How does automation reduce operational costs?',
                content: 'Automation minimizes manual tasks, prevents downtime, and optimizes resource usage, helping you control costs and improve efficiency.'
            },
            {
                id: 3,
                heading: 'Can you help with serverless architecture deployment?',
                content: 'Yes, we help design and deploy serverless solutions for scalable, event-driven applications with minimal infrastructure management.'
            },
            {
                id: 4,
                heading: 'Do you provide monitoring and support after deployment?',
                content: 'Yes, we offer continuous monitoring, updates, and support to ensure your cloud automation workflows run efficiently and securely.'
            }
        ]
    },
    {
        id: 4,
        image: images.service4,
        title: 'AI Integration',
        content: 'Integrate AI into your existing systems to enhance efficiency, innovation, and scalability.',
        paragraphs: [
            'Enhance your business processes by seamlessly embedding artificial intelligence into your current workflows and platforms. Our integration solutions enable smarter decision-making, automation, and improved user experiences.',
            'Whether it’s recommendation engines, chatbots, or predictive analytics, we ensure AI is smoothly integrated without disrupting your existing infrastructure.'
        ],
        gallery: [
            images.service4Gallery1,
            images.service4Gallery2
        ],
        features: [
            'Seamless System Integration',
            'Real-Time Data Processing',
            'Intelligent Automation Workflows',
            'Enhanced User Experience with AI Features',
            'Scalable & Secure Deployments'
        ],
        process: [
            'Assess Existing Infrastructure & AI Readiness',
            'Identify Integration Opportunities',
            'Develop & Train AI Models as Needed',
            'Integrate AI Components into Systems',
            'Monitor & Optimize Performance Continuously'
        ],
        faqs: [
            {
                id: 1,
                heading: 'What types of AI can you integrate into my systems?',
                content: 'We integrate machine learning models, NLP, computer vision, chatbots, recommendation systems, and predictive analytics into your workflows.'
            },
            {
                id: 2,
                heading: 'Will AI integration disrupt my current systems?',
                content: 'No, we ensure smooth integration with minimal downtime and maintain compatibility with your existing platforms and processes.'
            },
            {
                id: 3,
                heading: 'Can AI integration scale with my business growth?',
                content: 'Yes, our solutions are designed to scale as your business grows, supporting increasing workloads and new use cases.'
            },
            {
                id: 4,
                heading: 'How do you ensure the security of integrated AI systems?',
                content: 'We follow best practices for secure API integration, data privacy, and access control to safeguard your systems and data.'
            }
        ]
    },
    {
        id: 5,
        image: images.service5,
        title: 'Predictive Analytics',
        content: 'Forecast future trends and behaviors to drive better decision-making and business growth.',
        paragraphs: [
            'Leverage your historical data to uncover patterns and predict future outcomes. Our predictive analytics solutions empower your business with actionable insights that guide smarter strategies and reduce uncertainty.',
            'We use machine learning models and statistical techniques to help you anticipate customer behavior, optimize operations, and make proactive business decisions.'
        ],
        gallery: [
            images.service5Gallery1,
            images.service5Gallery2
        ],
        features: [
            'Trend & Pattern Detection',
            'Sales & Demand Forecasting',
            'Customer Behavior Prediction',
            'Operational Risk Assessment',
            'Real-Time Predictive Dashboards'
        ],
        process: [
            'Collect & Prepare Historical Data',
            'Analyze Trends & Identify Key Variables',
            'Build & Train Predictive Models',
            'Deploy Analytics to Business Tools',
            'Continuously Monitor & Refine Predictions'
        ],
        faqs: [
            {
                id: 1,
                heading: 'What industries benefit from predictive analytics?',
                content: 'Industries such as retail, finance, healthcare, logistics, and marketing use predictive analytics to improve forecasting and decision-making.'
            },
            {
                id: 2,
                heading: 'Do I need a large dataset for predictive analytics?',
                content: 'While larger datasets improve accuracy, we can generate valuable insights from smaller data sets using the right techniques.'
            },
            {
                id: 3,
                heading: 'How accurate are your predictive models?',
                content: 'Model accuracy depends on data quality and complexity, but we continuously test and refine models for the best possible performance.'
            },
            {
                id: 4,
                heading: 'Can predictive analytics be integrated into dashboards?',
                content: 'Yes, we integrate predictions into your business dashboards for real-time monitoring and decision-making.'
            }
        ]
    },
    {
        id: 6,
        image: images.service6,
        title: 'Support Services',
        content: 'Ensure seamless operation with 24/7 technical and customer support services.',
        paragraphs: [
            'Our dedicated support team is available around the clock to assist with technical issues, system monitoring, and user queries. We help you maintain uptime and performance without disruptions.',
            'From troubleshooting to continuous improvement suggestions, we proactively manage your systems to keep your operations running smoothly and efficiently.'
        ],
        gallery: [
            images.service6Gallery1,
            images.service6Gallery2
        ],
        features: [
            '24/7 Technical Support',
            'Proactive System Monitoring',
            'Incident Response & Resolution',
            'User Assistance & Guidance',
            'Performance Optimization'
        ],
        process: [
            'Set Up Support Channels & SLAs',
            'Monitor Systems & Usage Continuously',
            'Respond to Technical Incidents Promptly',
            'Provide Solutions & Troubleshooting',
            'Analyze Performance & Recommend Improvements'
        ],
        faqs: [
            {
                id: 1,
                heading: 'What type of support do you offer?',
                content: 'We offer technical troubleshooting, proactive monitoring, system optimization, and end-user assistance to keep your systems operational.'
            },
            {
                id: 2,
                heading: 'How do I reach your support team?',
                content: 'You can reach our support team via email, phone, or live chat, available 24/7 to assist you whenever needed.'
            },
            {
                id: 3,
                heading: 'Do you provide performance optimization services?',
                content: 'Yes, we monitor and optimize your systems regularly to ensure peak performance and stability.'
            },
            {
                id: 4,
                heading: 'Is your support customized to my solution?',
                content: 'Yes, we tailor our support services to meet the specific needs of your deployed AI solutions and business processes.'
            }
        ]
    }

];

export default services;